import { createFhirResource } from '@/fhirworks';
import { formatDate, formatPartialDate } from '@/filters/dateTimeFilters';
import { PRINT_AS_OF } from '@/components/questionnaires/form/print/printAsOf';
import { differenceInYears, isValid, parseISO } from 'date-fns';
import { inject, unref } from 'vue';

// Shared data helpers for the widget print components. Each print component mirrors the
// display logic of its live widget, and these are the parts every one of them needs.

/**
 * Hydrate a plain resource object into its fhirworks model so print components can read the
 * same getters the live widget reads.
 *
 * BnFormItemControl.getWorkingResponse() hydrates an answer that is a single resource, so a
 * widget storing one resource (demographic) arrives ready to use. An answer holding an array
 * of resources — which most clinical widgets store, since they sync their whole DataProvider
 * result onto the response — arrives as parsed JSON, so hydrate those here.
 */
export const hydrateResource = (value) => (value?.resourceType ? createFhirResource(value.resourceType, value) : value || null);

/**
 * The resources a widget's answer holds, hydrated, optionally narrowed to one resource type.
 * Accepts either shape an answer can take: a bare resource or an array of them.
 */
export const answerResources = (answer, resourceType = null) => {
    const list = (Array.isArray(answer) ? answer : [answer]).filter((entry) => entry && typeof entry === 'object' && entry.resourceType);
    const matching = resourceType ? list.filter((entry) => entry.resourceType === resourceType) : list;
    return matching.map(hydrateResource);
};

/** The first resource of a type in a widget's answer, hydrated. */
export const answerResource = (answer, resourceType) => answerResources(answer, resourceType)[0] || null;

/**
 * Drop facts with no value rather than printing the live widget's "-" placeholder: dashes
 * strung together by separator dots read as damage on a printed record.
 */
export const factList = (facts) => facts.filter((fact) => fact.value !== null && fact.value !== undefined && String(fact.value).trim() !== '');

// A FHIR date stated to the year or the month.
const PARTIAL_DATE = /^\d{4}(-\d{2})?$/;

/**
 * Format a date for print the way the live widgets show it: a year or a month/year keeps its
 * precision (an onset of "2015" prints 2015, not 01/01/2015), and the free text some widgets
 * store in a date field (an allergy onset of "Spring 2015") prints as written rather than
 * taking down the whole print render.
 */
export const printDate = (value) => {
    if (!value) return null;
    try {
        if (typeof value === 'string' && PARTIAL_DATE.test(value)) return formatPartialDate(value) || value;
        return formatDate(value) || value;
    } catch {
        return value;
    }
};

/** Sentence-case a code value the live widget capitalizes for display (gender, status). */
export const capitalize = (value) => (value ? String(value)[0].toUpperCase() + String(value).slice(1) : value);

const toDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : parseISO(String(value));
    return isValid(date) ? date : null;
};

/**
 * The moment the printed form describes: its lock time when BnFormRender provides one, else now.
 * Reads an injection, so call it in a print component's setup.
 */
export const printAsOf = () => toDate(unref(inject(PRINT_AS_OF, null))) || new Date();

/** Whole years from a birth date to `asOf` (now when null); null without a birth date. */
export const ageAsOf = (birthDate, asOf) => {
    const born = toDate(birthDate);
    if (!born) return null;
    return differenceInYears(toDate(asOf) || new Date(), born);
};
